import { ago, iso, NOW_MS } from "../time";
import type { BusyBlock, Calendar, CalendarEvent } from "../types/calendar";

/**
 * The calendar fixture world: the working week around the pinned "now"
 * (Thursday 22 October 2026, 14:20 UTC), across a Google Calendar account and
 * an Outlook Calendar account, so every screen shows the unification rather
 * than one provider.
 *
 * Times are absolute rather than relative to `NOW`, because a week grid has to
 * land on real weekday columns. The week is Monday 19 to Sunday 25 October.
 */

/** An instant on a day in October 2026, in UTC. */
function at(day: number, hour: number, minute = 0): string {
  return iso(Date.UTC(2026, 9, day, hour, minute));
}

/** A date-only string, as every provider stores an all-day boundary. */
function date(day: number): string {
  return `2026-10-${String(day).padStart(2, "0")}`;
}

/** The Monday of the week the pinned "now" sits in. */
export const WEEK_START_MS = Date.UTC(2026, 9, 19);
export const WEEK_END_MS = Date.UTC(2026, 9, 26);

export const CALENDARS: Calendar[] = [
  {
    id: "cal_maya_primary",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    name: "Maya Rao",
    owner: "maya@northwind.io",
    timeZone: "Europe/London",
    primary: true,
    access: "owner",
    color: "#6366f1",
    selected: true,
  },
  {
    id: "cal_nw_sales",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    name: "Northwind — Sales team",
    owner: "sales@northwind.io",
    timeZone: "Europe/London",
    primary: false,
    access: "writer",
    color: "#ec4899",
    selected: true,
  },
  {
    id: "cal_nw_rooms",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    name: "Rooms — Clerkenwell",
    owner: "rooms@northwind.io",
    timeZone: "Europe/London",
    primary: false,
    access: "reader",
    color: "#22d3ee",
    selected: false,
  },
  {
    id: "cal_dan_primary",
    accountId: "acc_oc_dan",
    provider: "outlook-calendar",
    name: "Dan Whitfield",
    owner: "dan@northwind.io",
    timeZone: "Europe/London",
    primary: true,
    access: "owner",
    color: "#f59e0b",
    selected: true,
  },
  {
    /**
     * The one calendar Aux0 can only ask free/busy of. It exists so the UI has
     * to answer "busy, and that is all you are allowed to know" rather than
     * assuming every calendar yields titles.
     */
    id: "cal_contoso_omar",
    accountId: "acc_oc_dan",
    provider: "outlook-calendar",
    name: "Omar Haddad (Contoso)",
    owner: "omar.haddad@contoso.com",
    timeZone: "Europe/Berlin",
    primary: false,
    access: "free_busy",
    color: "#8a8a95",
    selected: true,
  },
];

export const CALENDAR_BY_ID = new Map(CALENDARS.map((cal) => [cal.id, cal]));

const maya = {
  email: "maya@northwind.io",
  name: "Maya Rao",
  response: "accepted" as const,
};
const dan = {
  email: "dan@northwind.io",
  name: "Dan Whitfield",
  response: "accepted" as const,
};

export const EVENTS: CalendarEvent[] = [
  // ── Monday ────────────────────────────────────────────────────────────────
  {
    id: "evt_standup_mon",
    calendarId: "cal_nw_sales",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "Sales standup",
    start: at(19, 8, 30),
    end: at(19, 8, 45),
    allDay: false,
    status: "confirmed",
    organizer: dan,
    attendees: [dan, maya],
    selfResponse: "accepted",
    recurrence: "RRULE:FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR",
    recurringEventId: "evt_standup",
    conference: {
      provider: "Google Meet",
      url: "https://meet.google.com/qrt-mnvx-abc",
    },
    reminders: [5],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(14, "w"),
    updatedAt: ago(14, "w"),
  },
  {
    id: "evt_pipeline_review",
    calendarId: "cal_maya_primary",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "Pipeline review — EMEA",
    description:
      "Walk the forty-two open opportunities, agree which three are real for the quarter.",
    location: "Clerkenwell — Foundry (6)",
    start: at(19, 13, 0),
    end: at(19, 14, 0),
    allDay: false,
    status: "confirmed",
    organizer: maya,
    attendees: [
      maya,
      dan,
      {
        email: "sofia@vela.it",
        name: "Sofia Marchetti",
        response: "tentative",
      },
      {
        email: "aisha@northwind.io",
        name: "Aisha Kone",
        response: "needs_action",
        optional: true,
      },
      {
        email: "foundry@northwind.io",
        name: "Foundry (6)",
        response: "accepted",
        resource: true,
      },
    ],
    selfResponse: "accepted",
    reminders: [10],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(3, "w"),
    updatedAt: ago(5, "d"),
  },

  // ── Tuesday ───────────────────────────────────────────────────────────────
  {
    id: "evt_standup_tue",
    calendarId: "cal_nw_sales",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "Sales standup",
    start: at(20, 8, 30),
    end: at(20, 8, 45),
    allDay: false,
    status: "confirmed",
    organizer: dan,
    attendees: [dan, maya],
    selfResponse: "accepted",
    recurringEventId: "evt_standup",
    reminders: [5],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(14, "w"),
    updatedAt: ago(14, "w"),
  },
  {
    id: "evt_vela_discovery",
    calendarId: "cal_maya_primary",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "Vela — discovery call",
    description:
      "Booked from the Northwind trial form. Scheduled through the Aux0 API, so the hold, the invite and the Meet link are all ours.",
    start: at(20, 15, 30),
    end: at(20, 16, 15),
    allDay: false,
    status: "confirmed",
    organizer: maya,
    attendees: [
      maya,
      { email: "sofia@vela.it", name: "Sofia Marchetti", response: "accepted" },
    ],
    selfResponse: "accepted",
    conference: {
      provider: "Google Meet",
      url: "https://meet.google.com/xkd-ppma-nzq",
    },
    reminders: [10, 60],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(8, "d"),
    updatedAt: ago(8, "d"),
    viaApi: true,
  },
  {
    id: "evt_dan_1on1",
    calendarId: "cal_dan_primary",
    accountId: "acc_oc_dan",
    provider: "outlook-calendar",
    title: "Dan / Priya 1:1",
    start: at(20, 10, 0),
    end: at(20, 10, 30),
    allDay: false,
    status: "confirmed",
    organizer: dan,
    attendees: [
      dan,
      { email: "priya@northwind.io", name: "Priya Shah", response: "accepted" },
    ],
    selfResponse: "accepted",
    conference: {
      provider: "Microsoft Teams",
      url: "https://teams.microsoft.com/l/meetup-join/19%3ameeting_nw",
      phone: "+44 20 7946 0991",
    },
    reminders: [15],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(12, "w"),
    updatedAt: ago(12, "w"),
  },

  // ── Wednesday ─────────────────────────────────────────────────────────────
  {
    id: "evt_standup_wed",
    calendarId: "cal_nw_sales",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "Sales standup",
    start: at(21, 8, 30),
    end: at(21, 8, 45),
    allDay: false,
    status: "confirmed",
    organizer: dan,
    attendees: [dan, maya],
    selfResponse: "accepted",
    recurringEventId: "evt_standup",
    reminders: [5],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(14, "w"),
    updatedAt: ago(14, "w"),
  },
  {
    id: "evt_security_walkthrough",
    calendarId: "cal_maya_primary",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "Northwind × Aux0 — security walkthrough",
    description:
      "The questionnaire and the DPA, both sides on the call. Thread: Northwind — security questionnaire + DPA.",
    start: at(21, 14, 0),
    end: at(21, 15, 0),
    allDay: false,
    status: "confirmed",
    organizer: {
      email: "trust@aux0.com",
      name: "Aux0 Trust",
      response: "accepted",
    },
    attendees: [
      { email: "trust@aux0.com", name: "Aux0 Trust", response: "accepted" },
      maya,
      {
        email: "lena@northwind.io",
        name: "Lena Fischer",
        response: "accepted",
      },
    ],
    selfResponse: "accepted",
    conference: {
      provider: "Zoom",
      url: "https://aux0.zoom.us/j/8820114470",
      phone: "+44 330 088 5830",
    },
    reminders: [10],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(9, "d"),
    updatedAt: ago(2, "d"),
  },
  {
    id: "evt_board_prep",
    calendarId: "cal_dan_primary",
    accountId: "acc_oc_dan",
    provider: "outlook-calendar",
    title: "Board pack — first pass",
    start: at(21, 16, 0),
    end: at(21, 17, 30),
    allDay: false,
    status: "tentative",
    organizer: dan,
    attendees: [dan],
    selfResponse: "tentative",
    reminders: [],
    guestsCanSeeOtherGuests: false,
    createdAt: ago(6, "d"),
    updatedAt: ago(6, "d"),
  },

  // ── Thursday, the pinned day ──────────────────────────────────────────────
  {
    id: "evt_standup_thu",
    calendarId: "cal_nw_sales",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "Sales standup",
    start: at(22, 8, 30),
    end: at(22, 8, 45),
    allDay: false,
    status: "confirmed",
    organizer: dan,
    attendees: [dan, maya],
    selfResponse: "accepted",
    recurringEventId: "evt_standup",
    reminders: [5],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(14, "w"),
    updatedAt: ago(14, "w"),
  },
  {
    id: "evt_now_in_progress",
    calendarId: "cal_maya_primary",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "Sablehq — renewal",
    description:
      "Omar's mailbox disconnected nine days ago, so nothing in the thread has synced since. Ask him to reconnect on the call.",
    start: at(22, 14, 0),
    end: at(22, 14, 45),
    allDay: false,
    status: "confirmed",
    organizer: maya,
    attendees: [
      maya,
      {
        email: "omar@sablehq.com",
        name: "Omar Haddad",
        response: "accepted",
      },
    ],
    selfResponse: "accepted",
    conference: {
      provider: "Google Meet",
      url: "https://meet.google.com/bbs-khtq-fwz",
    },
    reminders: [5],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(11, "d"),
    updatedAt: ago(1, "d"),
  },
  {
    /** An invitation that has not been answered: the one row with an action. */
    id: "evt_unanswered_invite",
    calendarId: "cal_maya_primary",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "CASA Tier 2 — evidence review",
    description:
      "The assessor wants the scope justifications and the retention settings before the call.",
    start: at(22, 16, 30),
    end: at(22, 17, 30),
    allDay: false,
    status: "confirmed",
    organizer: {
      email: "assessor@casa-example.org",
      name: "CASA assessor",
      response: "accepted",
    },
    attendees: [
      {
        email: "assessor@casa-example.org",
        name: "CASA assessor",
        response: "accepted",
      },
      { ...maya, response: "needs_action" },
      {
        email: "lena@northwind.io",
        name: "Lena Fischer",
        response: "accepted",
        optional: true,
      },
    ],
    selfResponse: "needs_action",
    reminders: [30],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(4, "h"),
    updatedAt: ago(4, "h"),
  },
  {
    /** Cancelled but still in the window: a sync that drops it loses the tombstone. */
    id: "evt_cancelled_qbr",
    calendarId: "cal_maya_primary",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "QBR — Contoso",
    start: at(22, 11, 0),
    end: at(22, 12, 0),
    allDay: false,
    status: "cancelled",
    organizer: {
      email: "omar.haddad@contoso.com",
      name: "Omar Haddad",
      response: "declined",
    },
    attendees: [
      {
        email: "omar.haddad@contoso.com",
        name: "Omar Haddad",
        response: "declined",
      },
      maya,
    ],
    selfResponse: "accepted",
    reminders: [],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(3, "w"),
    updatedAt: ago(26, "h"),
  },

  // ── Friday ────────────────────────────────────────────────────────────────
  {
    id: "evt_standup_fri",
    calendarId: "cal_nw_sales",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "Sales standup",
    start: at(23, 8, 30),
    end: at(23, 8, 45),
    allDay: false,
    status: "confirmed",
    organizer: dan,
    attendees: [dan, maya],
    selfResponse: "accepted",
    recurringEventId: "evt_standup",
    reminders: [5],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(14, "w"),
    updatedAt: ago(14, "w"),
  },
  {
    /** All-day, so `start` and `end` are dates and `end` is exclusive. */
    id: "evt_lena_pto",
    calendarId: "cal_nw_sales",
    accountId: "acc_gc_maya",
    provider: "google-calendar",
    title: "Lena — out of office",
    start: date(23),
    end: date(26),
    allDay: true,
    status: "confirmed",
    organizer: {
      email: "lena@northwind.io",
      name: "Lena Fischer",
      response: "accepted",
    },
    attendees: [],
    reminders: [],
    guestsCanSeeOtherGuests: false,
    createdAt: ago(5, "w"),
    updatedAt: ago(5, "w"),
  },
  {
    id: "evt_eng_demo",
    calendarId: "cal_dan_primary",
    accountId: "acc_oc_dan",
    provider: "outlook-calendar",
    title: "Engine 3 demo — IMAP reconcile",
    description: "UIDVALIDITY changing under a live cursor, and what the client sees.",
    start: at(23, 15, 0),
    end: at(23, 16, 0),
    allDay: false,
    status: "confirmed",
    organizer: dan,
    attendees: [
      dan,
      {
        email: "lena@northwind.io",
        name: "Lena Fischer",
        response: "accepted",
      },
      { email: "priya@northwind.io", name: "Priya Shah", response: "tentative" },
    ],
    selfResponse: "accepted",
    conference: {
      provider: "Microsoft Teams",
      url: "https://teams.microsoft.com/l/meetup-join/19%3ameeting_demo",
    },
    reminders: [10],
    guestsCanSeeOtherGuests: true,
    createdAt: ago(2, "d"),
    updatedAt: ago(2, "d"),
  },
];

export const EVENT_BY_ID = new Map(EVENTS.map((event) => [event.id, event]));

/**
 * The free/busy-only calendar's blocks. No titles, no attendees: this is
 * everything the provider will tell Aux0 about that calendar, and a screen has
 * to be honest about it.
 */
export const BUSY_BLOCKS: BusyBlock[] = [
  { calendarId: "cal_contoso_omar", start: at(19, 9, 0), end: at(19, 11, 0) },
  { calendarId: "cal_contoso_omar", start: at(20, 13, 0), end: at(20, 17, 0) },
  { calendarId: "cal_contoso_omar", start: at(21, 8, 0), end: at(21, 9, 30) },
  { calendarId: "cal_contoso_omar", start: at(22, 9, 0), end: at(22, 12, 30) },
  { calendarId: "cal_contoso_omar", start: at(22, 15, 0), end: at(22, 18, 0) },
  { calendarId: "cal_contoso_omar", start: at(23, 10, 0), end: at(23, 12, 0) },
];

/** Whether the pinned "now" falls inside an event: the "in progress" marker. */
export function isInProgress(event: CalendarEvent): boolean {
  if (event.allDay) return false;
  return Date.parse(event.start) <= NOW_MS && NOW_MS < Date.parse(event.end);
}
